import { p as proxyCustomElement, H, c as createEvent, h, a as Host } from './index.js';
import { t as toDateRange, s as stringifyValue, p as parseValue, W as WuiDateRangeInput, i as isRangeBeforeDate, a as isRangeAfterDate, e as exceedsMaxDays, d as defineCustomElement$j } from './p-C2Br9aSV.js';
import { g as getValidityObject, c as checkValidity, r as reportValidity, s as setCustomValidity, a as setInputRef, b as getError, w as watchError, d as watchWarning, e as getFormFieldAttributes, f as getHostAttributes } from './p-De5ZxJ78.js';
import { i as isKeyOfObject } from './p-Dnii66ei.js';
import { d as dateFnsExports } from './p-iWCgRMLC.js';
import { d as defineCustomElement$q } from './p-RsIZ6iMr.js';
import { d as defineCustomElement$p } from './p-BbTg3Car.js';
import { d as defineCustomElement$o } from './p-D5W-FK48.js';
import { d as defineCustomElement$n } from './p-BwWHobpA.js';
import { d as defineCustomElement$m } from './p-sF9rvBC9.js';
import { d as defineCustomElement$l } from './p-D2ldz6Gz.js';
import { d as defineCustomElement$k } from './p-DzykiKjP.js';
import { d as defineCustomElement$i } from './p-CgjIzafk.js';
import { d as defineCustomElement$h } from './p-RWl7wfjL.js';
import { d as defineCustomElement$g } from './p-BDIehgMZ.js';
import { d as defineCustomElement$f } from './p-BYuH_pKM.js';
import { d as defineCustomElement$e } from './p-DAbs3-K7.js';
import { d as defineCustomElement$d } from './p-OveG5gGw.js';
import { d as defineCustomElement$c } from './p-DKss2Fy6.js';
import { d as defineCustomElement$b } from './p-DMGlYwB7.js';
import { d as defineCustomElement$a } from './p-DvXL3Xv_.js';
import { d as defineCustomElement$9 } from './p-CEikBRMP.js';
import { d as defineCustomElement$8 } from './p-Bi4IrIfv.js';
import { d as defineCustomElement$7 } from './p-w5YFouGP.js';
import { d as defineCustomElement$6 } from './p-lnuA9A6c.js';
import { d as defineCustomElement$5 } from './p-2MYQ9zL1.js';
import { d as defineCustomElement$4 } from './p-BaLMNd0l.js';
import { d as defineCustomElement$3 } from './p-CW-Ftoto.js';
import { d as defineCustomElement$2 } from './p-Trmgzgcp.js';
import { d as defineCustomElement$1 } from './p-KbTC_jnR.js';

/**
 * Note that by default our presets exclude today, so we start with 'yesterday' instead of 'today'
 */
const getDateRangePresets = (excludeToday = true) => {
    const today = new Date();
    const yesterday = dateFnsExports.subDays(today, 1);
    const end = excludeToday ? yesterday : today;
    const start = excludeToday ? today : dateFnsExports.addDays(today, 1);
    return {
        TODAY: {
            label: 'Today',
            value: () => toDateRange([today, today]),
        },
        YESTERDAY: {
            label: 'Yesterday',
            value: () => toDateRange([yesterday, yesterday]),
        },
        '7_DAYS': {
            label: 'Last 7 days',
            value: () => toDateRange([dateFnsExports.subDays(start, 7), end]),
        },
        '14_DAYS': {
            label: 'Last 14 days',
            value: () => toDateRange([dateFnsExports.subDays(start, 14), end]),
        },
        '30_DAYS': {
            label: 'Last 30 days',
            value: () => toDateRange([dateFnsExports.subDays(start, 30), end]),
        },
        '60_DAYS': {
            label: 'Last 60 days',
            value: () => toDateRange([dateFnsExports.subDays(start, 60), end]),
        },
        '90_DAYS': {
            label: 'Last 90 days',
            value: () => toDateRange([dateFnsExports.subDays(start, 90), end]),
        },
        '12_MONTHS': {
            label: 'Last 12 months',
            value: () => toDateRange([dateFnsExports.subMonths(end, 12), end]),
        },
        '24_MONTHS': {
            label: 'Last 24 months',
            value: () => toDateRange([dateFnsExports.subMonths(end, 24), end]),
        },
        '60_MONTHS': {
            label: 'Last 60 months',
            value: () => toDateRange([dateFnsExports.subMonths(end, 60), end]),
        },
        MONTH_TO_DATE: {
            label: 'Month to date',
            value: () => toDateRange([dateFnsExports.startOfMonth(today), end]),
        },
        CUSTOM: {
            label: 'Custom',
            value: () => toDateRange([null, null]),
        },
    };
};

const dateRangeInputWithPresetsCss = ":host{-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;box-sizing:border-box;font-family:Open Sans;font-size:.875rem;font-weight:400;letter-spacing:-.0004em;line-height:1.25rem;line-height:normal}:host *,:host :after,:host :before{box-sizing:inherit}:host img{flex:none;overflow:hidden;text-overflow:ellipsis;white-space:pre-line}wui-select-input{width:var(--jw-container-xs)}";

const isRangeInvalid = (range, opts) => {
    return (isRangeBeforeDate(range, opts?.minDate) ||
        isRangeAfterDate(range, opts?.maxDate) ||
        exceedsMaxDays(range, opts?.maxDays));
};
const WuiDateRangeInputWithPresets = /*@__PURE__*/ proxyCustomElement(class WuiDateRangeInputWithPresets extends H {
    constructor() {
        super();
        this.__registerHost();
        this.__attachShadow();
        this.validityChecked = createEvent(this, "validityChecked", 7);
        this.validityReported = createEvent(this, "validityReported", 7);
        this.required = false;
        this.validity = getValidityObject();
        this.validationMessage = '';
        this.customValidation = undefined;
        this.checkValidity = checkValidity(this);
        this.reportValidity = reportValidity(this, () => {
            if (!this.elm.shadowRoot) {
                return;
            }
            return this.elm.shadowRoot.querySelectorAll('wui-date-range-input');
        });
        this.setCustomValidity = setCustomValidity(this);
        this.showValidity = false;
        this.setInputRef = setInputRef(this);
        this.dirty = false;
        this.getError = getError(this);
        this.validateInternal = () => {
            const dateRangeInputElm = this.elm.shadowRoot?.querySelector('wui-date-range-input');
            if (!dateRangeInputElm) {
                return;
            }
            // Let inner input handle validation
            dateRangeInputElm.checkValidity();
            return {
                validationMessage: '', // Keep empty as the inner inputs will report their validity
                validity: dateRangeInputElm.validity,
            };
        };
        /**
         * Note that by default our presets exclude today, so we start with 'yesterday' instead of 'today'
         */
        this.presets = getDateRangePresets();
        /**
         * Automatically filter presets that fall outside of given limits.
         *
         * If presets are not filtered, then the date-range-input will automatically be disabled when
         * presets are found that violate the limits. Otherwise the user can get into erroneous states.
         */
        this.autoFilterPresets = true;
        this.selectedPresetName = 'CUSTOM';
        this.handlePresetChange = async (e) => {
            // We fire our own input event
            e.stopPropagation();
            e.stopImmediatePropagation();
            const newPreset = e.target?.value;
            if (!isKeyOfObject(newPreset, this.filteredPresets)) {
                return;
            }
            this.selectedPresetName = newPreset;
            const foundPreset = this.filteredPresets[newPreset];
            if (!foundPreset) {
                throw new Error('Undefined: foundPreset');
            }
            // Allow Stencil to re-render the date-range-input before updating value and re-triggering validation
            await new Promise((r) => setTimeout(r, 1));
            let newValue;
            // Keep existing value when switching to custom and is within our limits
            if (this.selectedPresetName === 'CUSTOM' &&
                // Check if existing value is within limits
                this.valueAsDateRange &&
                !isRangeInvalid(this.valueAsDateRange, this.dateRangeInput)) {
                // We do want to call `updateValue` to re-trigger our input event.
                // The date range may be the same, but the preset changed.
                newValue = this.value;
            }
            else {
                newValue = WuiDateRangeInput.stringifyValue(foundPreset.value());
            }
            this.updateValue(newValue);
        };
        this.handleDateRangeChange = (e) => {
            // We fire our own input event
            e.stopPropagation();
            e.stopImmediatePropagation();
            if (this.selectedPresetName !== 'CUSTOM') {
                this.selectedPresetName = 'CUSTOM';
            }
            const value = e.target?.value;
            if (!value) {
                return;
            }
            this.updateValue(value);
        };
    }
    _watchError(...args) {
        watchError(this, ...args);
    }
    _watchWarning(...args) {
        watchWarning(this, ...args);
    }
    get selectedPreset() {
        return this.selectedPresetName && this.selectedPresetName in this.filteredPresets
            ? this.filteredPresets[this.selectedPresetName]
            : undefined;
    }
    /**
     * Filter presets based on restrictions. This property should always be prefered to directly reading `this.presets`.
     */
    get filteredPresets() {
        if (!this.autoFilterPresets) {
            return this.presets;
        }
        return Object.entries(this.presets).reduce((acc, [name, preset]) => {
            // Only add when preset is valid
            if (!this.isPresetInvalid(preset)) {
                acc[name] = preset;
            }
            return acc;
        }, {});
    }
    isPresetInvalid(preset = this.selectedPreset) {
        return preset ? isRangeInvalid(preset.value(), this.dateRangeInput) : false;
    }
    findPresetNameForRange(rangeString) {
        return Object.keys(this.filteredPresets).find((presetName) => {
            const preset = this.filteredPresets[presetName];
            if (!preset) {
                return;
            }
            const presetRangeString = WuiDateRangeInput.stringifyValue(preset.value());
            return rangeString === presetRangeString;
        });
    }
    updateValue(newValue) {
        this.value = newValue;
        this.elm.dispatchEvent(new window.Event('input', { bubbles: true }));
        this.dirty = true;
        this.checkValidity();
    }
    componentWillLoad() {
        if (!this.value) {
            return;
        }
        const foundPreset = this.findPresetNameForRange(this.value);
        if (foundPreset) {
            this.selectedPresetName = foundPreset;
        }
    }
    render() {
        return (h(Host, { key: '616e20659934d3ebe0b79ea89c2fd1cccae7f361', ...getHostAttributes(this) }, h("wui-form-field", { key: '1300fdcda8d161ae020dee1b363fa02465928e1f', ...getFormFieldAttributes(this) }, h("wui-layout", { key: '26b92f5d4c7f350d0bbb81ec375c7305c9ba882a', type: "field-field-group" }, h("wui-select-input", { key: '1c867689adcfb8f678c3f2338833505a1820f65a', options: Object.entries(this.filteredPresets).map(([presetName, preset]) => {
                return {
                    label: preset.label,
                    value: presetName,
                };
            }), value: this.selectedPresetName, onInput: this.handlePresetChange, disabled: this.disabled, formField: {
                label: 'Timeframe',
            }, ...this.selectInput }), h("wui-date-range-input", { key: '320b10c5316eb309cab9ffef214fd3aed6555f56', ...this.dateRangeInput, required: this.required, disabled: this.disabled, readOnly: this.selectedPresetName !== 'CUSTOM', value: this.value, onInput: this.handleDateRangeChange })))));
    }
    get elm() { return this; }
    static get watchers() { return {
        "error": ["_watchError"],
        "warning": ["_watchWarning"]
    }; }
    static get style() { return dateRangeInputWithPresetsCss; }
}, [257, "wui-date-range-input-with-presets", {
        "value": [1025],
        "required": [8],
        "error": [8],
        "warning": [8],
        "validate": [16],
        "validity": [1040],
        "validationMessage": [1025, "validation-message"],
        "checkValidity": [16, "check-validity"],
        "reportValidity": [16, "report-validity"],
        "setCustomValidity": [16, "set-custom-validity"],
        "formField": [16, "form-field"],
        "name": [1],
        "disabled": [4],
        "dateRangeInput": [16, "date-range-input"],
        "selectInput": [16, "select-input"],
        "presets": [16],
        "autoFilterPresets": [4, "auto-filter-presets"],
        "valueAsDateRange": [1040, "value-as-date-range"],
        "valueAsPresetName": [1025, "value-as-preset-name"],
        "customValidation": [32],
        "showValidity": [32],
        "selectedPresetName": [32]
    }, undefined, {
        "error": ["_watchError"],
        "warning": ["_watchWarning"]
    }]);
WuiDateRangeInputWithPresets.PRESETS = getDateRangePresets;
WuiDateRangeInputWithPresets.stringifyValue = stringifyValue;
WuiDateRangeInputWithPresets.parseValue = parseValue;
WuiDateRangeInputWithPresets.isRangeInvalid = isRangeInvalid;
Object.defineProperties(WuiDateRangeInputWithPresets.prototype, {
    valueAsDateRange: {
        configurable: true,
        get() {
            return WuiDateRangeInput.parseValue(this.value);
        },
        set(newValue) {
            this.value = newValue ? WuiDateRangeInput.stringifyValue(newValue) : undefined;
            this.selectedPresetName = (this.value ? this.findPresetNameForRange(this.value) : undefined) ?? 'CUSTOM';
            this.dirty = true;
            this.checkValidity();
        },
    },
    valueAsPresetName: {
        configurable: true,
        get() {
            return this.selectedPresetName;
        },
        set(newValue) {
            const preset = newValue && newValue in this.filteredPresets ? this.filteredPresets[newValue] : undefined;
            if (!preset) {
                return;
            }
            this.value = WuiDateRangeInput.stringifyValue(preset.value());
            this.selectedPresetName = newValue;
            this.dirty = true;
            this.checkValidity();
        },
    },
});
function defineCustomElement() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["wui-date-range-input-with-presets", "wui-badge", "wui-box", "wui-button", "wui-check-input", "wui-close-button", "wui-date-input", "wui-date-input-calendar", "wui-date-range-input", "wui-divider", "wui-form-field", "wui-icon", "wui-input", "wui-input-button", "wui-input-menu", "wui-layout", "wui-list", "wui-list-item", "wui-list-item-divider", "wui-list-item-header", "wui-list-item-meta", "wui-loader", "wui-menu-pane", "wui-select-input", "wui-status", "wui-tooltip", "wui-tooltip-label"];
    components.forEach(tagName => { switch (tagName) {
        case "wui-date-range-input-with-presets":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, WuiDateRangeInputWithPresets);
            }
            break;
        case "wui-badge":
            if (!customElements.get(tagName)) {
                defineCustomElement$q();
            }
            break;
        case "wui-box":
            if (!customElements.get(tagName)) {
                defineCustomElement$p();
            }
            break;
        case "wui-button":
            if (!customElements.get(tagName)) {
                defineCustomElement$o();
            }
            break;
        case "wui-check-input":
            if (!customElements.get(tagName)) {
                defineCustomElement$n();
            }
            break;
        case "wui-close-button":
            if (!customElements.get(tagName)) {
                defineCustomElement$m();
            }
            break;
        case "wui-date-input":
            if (!customElements.get(tagName)) {
                defineCustomElement$l();
            }
            break;
        case "wui-date-input-calendar":
            if (!customElements.get(tagName)) {
                defineCustomElement$k();
            }
            break;
        case "wui-date-range-input":
            if (!customElements.get(tagName)) {
                defineCustomElement$j();
            }
            break;
        case "wui-divider":
            if (!customElements.get(tagName)) {
                defineCustomElement$i();
            }
            break;
        case "wui-form-field":
            if (!customElements.get(tagName)) {
                defineCustomElement$h();
            }
            break;
        case "wui-icon":
            if (!customElements.get(tagName)) {
                defineCustomElement$g();
            }
            break;
        case "wui-input":
            if (!customElements.get(tagName)) {
                defineCustomElement$f();
            }
            break;
        case "wui-input-button":
            if (!customElements.get(tagName)) {
                defineCustomElement$e();
            }
            break;
        case "wui-input-menu":
            if (!customElements.get(tagName)) {
                defineCustomElement$d();
            }
            break;
        case "wui-layout":
            if (!customElements.get(tagName)) {
                defineCustomElement$c();
            }
            break;
        case "wui-list":
            if (!customElements.get(tagName)) {
                defineCustomElement$b();
            }
            break;
        case "wui-list-item":
            if (!customElements.get(tagName)) {
                defineCustomElement$a();
            }
            break;
        case "wui-list-item-divider":
            if (!customElements.get(tagName)) {
                defineCustomElement$9();
            }
            break;
        case "wui-list-item-header":
            if (!customElements.get(tagName)) {
                defineCustomElement$8();
            }
            break;
        case "wui-list-item-meta":
            if (!customElements.get(tagName)) {
                defineCustomElement$7();
            }
            break;
        case "wui-loader":
            if (!customElements.get(tagName)) {
                defineCustomElement$6();
            }
            break;
        case "wui-menu-pane":
            if (!customElements.get(tagName)) {
                defineCustomElement$5();
            }
            break;
        case "wui-select-input":
            if (!customElements.get(tagName)) {
                defineCustomElement$4();
            }
            break;
        case "wui-status":
            if (!customElements.get(tagName)) {
                defineCustomElement$3();
            }
            break;
        case "wui-tooltip":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
        case "wui-tooltip-label":
            if (!customElements.get(tagName)) {
                defineCustomElement$1();
            }
            break;
    } });
}
defineCustomElement();

export { WuiDateRangeInputWithPresets as W, defineCustomElement as d };
